import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { filter } from 'ramda';
import moment from 'moment';
import IconChevronLeft from '~~elements/static/IconChevronLeft.svg';
import toast from '~~elements/Toast';
import { SeasonResource } from '~~apis/resource';
import { InlineImgIcon } from './styled';
import CTMultilingual from './Multilingual';
import { getPriceDisplay, getTotalPriceDisplay } from './service';
import { useCreateTournamentState, useCreateTournamentDispatch } from './useCreateTournament';
import Basic from './components/Basic';
import Plugins from './components/Plugins';
import PlanCheck from './components/PlanCheck';
import { StyledDiv } from '../Settings/Styles';

const CurrentPlanStyled = styled.div`
  width: 100%;
  > .plan-item {
    width: 100%;
    flex-wrap: wrap;
    > p {
      padding: 0 0.3rem;
    }
    > .--name {
      text-align: start;
      margin: 0;
    }
    > .--price {
      text-align: end;
      margin: 0 0 0.5rem;
    }
  }
`;

const CSInfoDiv = styled.div`
  display: block;
  width: 100%;
  > p {
    margin: 0.5rem 0;
  }

  margin-top: 1rem;
  padding: 0.3rem;
  border-radius: 5px;
  background-color: #fafafa;
`;

const CreateTournament = ({ organizer, locations, pluginsPrice }) => {
  const { section } = useParams();
  const { formData, plugins, contact, isFree, creatable, apiLoading, inPlanCheck } = useCreateTournamentState();
  const dispatch = useCreateTournamentDispatch();

  useEffect(() => {
    dispatch({ type: 'SET_PLAN_CHECK', payload: false });
  }, [dispatch, section]);

  useEffect(() => {
    dispatch({ type: 'UPDATE_PLUGINS_PRICE', payload: pluginsPrice });
  }, [dispatch, pluginsPrice]);

  const handlePluginSubmit = (e) => {
    if (e.preventDefault) {
      e.preventDefault();
    }

    if (!creatable) {
      return;
    }

    if (isFree) {
      dispatch({ type: 'UPDATE_API_STATUS', payload: true });
      SeasonResource.createBasicSeason({
        leagueUniqid: organizer.uniqid,
        data: {
          ...formData,
          start_at: moment(formData.start_at).format('YYYY/MM/DD'),
          end_at: moment(formData.end_at).format('YYYY/MM/DD'),
        },
      })
        .then(({ data: createdSeason }) => {
          toast(CTMultilingual(`SUCCESS.CREATE`), { status: 'success', second: 1 });
          setTimeout(() => {
            window.location.replace(`/organizer/${organizer.uniqid}/tournament/${createdSeason.uniqid}/basic`);
            dispatch({ type: 'UPDATE_API_STATUS', payload: false });
          }, 800);
        })
        .catch((e) => {
          console.warn(e);
          toast(CTMultilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error', second: 2 });
          dispatch({ type: 'UPDATE_API_STATUS', payload: false });
        });
      return;
    }

    dispatch({ type: 'SET_PLAN_CHECK', payload: true });
  };

  const planCheckBack = () => {
    dispatch({ type: 'SET_PLAN_CHECK', payload: false });
  };

  const handlePlanCheckSubmit = (e) => {
    if (e.preventDefault) {
      e.preventDefault();
    }

    if (!creatable) {
      return;
    }

    dispatch({ type: 'UPDATE_API_STATUS', payload: true });
    SeasonResource.createPremiumSeason({
      leagueUniqid: organizer.uniqid,
      data: {
        basic: {
          ...formData,
          start_at: moment(formData.start_at).format('YYYY/MM/DD'),
          end_at: moment(formData.end_at).format('YYYY/MM/DD'),
        },
        plugins,
        contact,
      },
    })
      .then(({ data: createdSeason }) => {
        toast(CTMultilingual(`SUCCESS.CREATE`), { status: 'success', second: 1 });
        setTimeout(() => {
          window.location.replace(`/organizer/${organizer.uniqid}/tournament/${createdSeason.uniqid}/basic`);
          dispatch({ type: 'UPDATE_API_STATUS', payload: false });
        }, 800);
      })
      .catch((e) => {
        console.warn(e);
        toast(CTMultilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error', second: 2 });
        dispatch({ type: 'UPDATE_API_STATUS', payload: false });
      });
  };

  const sectionLink = (targetSection) => {
    if ((section === 'basic' && targetSection !== 'basic') || apiLoading) {
      return (
        <label className={`section-list--item disabled`}>
          {CTMultilingual(`SIDEBAR.${targetSection.toUpperCase()}`)}
        </label>
      );
    }

    return (
      <Link
        className={`section-list--item ${section === targetSection && 'selected'}`}
        to={(location) => ({
          ...location,
          pathname: `/organizer/${organizer.uniqid}/create_tournament/${targetSection}`,
        })}
      >
        {CTMultilingual(`SIDEBAR.${targetSection.toUpperCase()}`)}
      </Link>
    );
  };

  const getSection = () => {
    switch (section) {
      case 'basic':
        return <Basic locations={locations} nextLink={`/organizer/${organizer.uniqid}/create_tournament/plugins`} />;
      case 'plugins':
        return (
          <Plugins
            pluginsPrice={pluginsPrice}
            backLink={`/organizer/${organizer.uniqid}/create_tournament/basic`}
            handleSubmit={handlePluginSubmit}
          />
        );
      default:
        return <></>;
    }
  };

  const getCSInfo = () => (
    <CSInfoDiv>
      <p>如有任何問題，歡迎透過電子信箱與我們聯繫</p>
      <p>聯絡信箱：rebastaiwan@gmail.com</p>
      <p>聯絡時間：周一至周五 08:00 - 18:00</p>
      <p>公司名稱：野革運動股份有限公司</p>
    </CSInfoDiv>
  );

  return (
    <StyledDiv>
      <div className="greet">
        {organizer.logo_url ? (
          <img className="greet--pic" src={organizer.logo_url} alt="" />
        ) : (
          <div className="greet--pic default"></div>
        )}
        <div className="greet--name">
          {organizer.name}
          {' - '}
          {CTMultilingual('CREATE_TOURNAMENT')}
        </div>
      </div>
      <div className="panel">
        <div className="multi-section-list">
          <div className="section-list">
            <div className="section-list--title" />
            <Link
              className="section-list--item"
              to={() => ({
                pathname: `/settings/organizer_tournament_management`,
              })}
            >
              <InlineImgIcon src={IconChevronLeft} alt="back" />
              {CTMultilingual(`ACCOUNT_MANAGEMENT.WORDING`)}
            </Link>
          </div>
          <div className="section-list">
            <div className="section-list--title" />
            {sectionLink('basic')}
            {sectionLink('plugins')}
          </div>
          <div className="section-list">
            <div className="section-list--title">{CTMultilingual(`CURRENT_PLAN`)}</div>
            <CurrentPlanStyled>
              <div className="plan-item">
                <p className="--name">{CTMultilingual(`PLAN_BASIC`)}</p>
                <p className="--price">
                  <i>{getPriceDisplay(pluginsPrice.currency, 0.0)}</i>
                </p>
              </div>
              {filter((pluginName) => plugins[pluginName], Object.keys(plugins)).map((pluginName) => (
                <div key={pluginName} className="plan-item">
                  <p className="--name">{CTMultilingual(`TOURNAMENT.PLUGIN.${pluginName.toUpperCase()}`)}</p>
                  <p className="--price">
                    <i>+{getPriceDisplay(pluginsPrice.currency, pluginsPrice[pluginName].retail_price)}</i>
                  </p>
                </div>
              ))}
              <p />
              <div className="plan-item">
                <p className="--name">{CTMultilingual(`PLAN_TOTAL`)}</p>
                <p className="--price">
                  <i>{getTotalPriceDisplay(pluginsPrice, plugins)}</i>
                </p>
              </div>
            </CurrentPlanStyled>
          </div>
        </div>
        {!inPlanCheck ? (
          <div className="section">
            <div className="section--title">{CTMultilingual(`SIDEBAR.${section.toUpperCase()}`)}</div>
            {getSection()}
            {getCSInfo()}
          </div>
        ) : (
          <div className="section">
            <div className="section--title">{CTMultilingual(`CURRENT_PLAN`)}</div>
            <PlanCheck back={planCheckBack} handleSubmit={handlePlanCheckSubmit} />
            {getCSInfo()}
          </div>
        )}
      </div>
    </StyledDiv>
  );
};

export default CreateTournament;
